import React, { useEffect, useState } from 'react';
import { Link, Navigate } from 'react-router-dom';
import { FaArrowRight, FaBookOpen } from 'react-icons/fa6';
import { useAuth } from '../lib/auth';
import { fetchJSON, postJSON } from '../lib/api';

const FIRST_LESSON = '/courses/ai-for-parents/module-1/intro';

function getLocalResumeHref(slug) {
  try {
    return localStorage.getItem(`lo:last_lesson:${slug}`) || FIRST_LESSON;
  } catch {
    return FIRST_LESSON;
  }
}

function SignOutPanel({ signOut }) {
  const [count, setCount] = useState(null);
  const [signingOut, setSigningOut] = useState(false);

  useEffect(() => {
    fetchJSON('/api/auth/sessions')
      .then((d) => setCount(d.sessions.length))
      .catch(() => setCount(1));
  }, []);

  async function handleSignOut() {
    setSigningOut(true);
    await signOut();
  }

  async function handleSignOutAll() {
    setSigningOut(true);
    try {
      await postJSON('/api/auth/logout-all', {});
    } catch (_) {}
    // Re-use the same full cleanup signOut does (clears localStorage, user state).
    await signOut();
  }

  if (count === null) return null;

  return (
    <div className="mt-10 pt-8 border-t border-ink/10 flex flex-wrap items-center justify-between gap-3">
      <p className="text-sm text-slate2">
        {count > 1 ? `Signed in on ${count} devices` : 'Signed in on this device'}
      </p>
      <div className="flex items-center gap-4">
        {count > 1 && (
          <button
            onClick={handleSignOutAll}
            disabled={signingOut}
            className="text-sm text-slate2 hover:text-ink underline underline-offset-2 disabled:opacity-50"
          >
            {signingOut ? 'Signing out…' : 'Sign out everywhere'}
          </button>
        )}
        <button
          onClick={handleSignOut}
          disabled={signingOut}
          className="text-sm text-slate2 hover:text-ink underline underline-offset-2 disabled:opacity-50"
        >
          {signingOut ? 'Signing out…' : 'Sign out'}
        </button>
      </div>
    </div>
  );
}

export default function MyCourses() {
  const { user, loading, signOut } = useAuth();
  const [enrolled, setEnrolled] = useState(null);
  const [resumeHref, setResumeHref] = useState(() => getLocalResumeHref('ai-for-parents'));

  useEffect(() => {
    document.title = 'My Courses — Learning Outcomes Academy';
    return () => { document.title = 'Learning Outcomes Academy'; };
  }, []);

  useEffect(() => {
    if (!user) return;
    fetchJSON('/api/progress/course/ai-for-parents/resume')
      .then((data) => {
        if (data && data.last_lesson_slug) setResumeHref(data.last_lesson_slug);
      })
      .catch(() => {});
  }, [user]);

  useEffect(() => {
    if (!user) return;
    fetch('/api/checkout/enrollment?course_slug=ai-for-parents', {
      credentials: 'include',
      headers: (() => {
        try {
          const sid = localStorage.getItem('_loa_dev_sid');
          return sid ? { Authorization: `Bearer ${sid}` } : {};
        } catch { return {}; }
      })(),
    })
      .then((r) => r.json())
      .then((d) => setEnrolled(d.enrolled))
      .catch(() => setEnrolled(false));
  }, [user]);

  if (loading) return null;
  if (!user) return <Navigate to="/login?next=/my-courses" replace />;

  return (
    <div className="w-full bg-bone min-h-[70vh]">
      <div className="container-page py-16 max-w-3xl">
        <div className="text-[11px] tracking-[0.28em] uppercase text-brand-aubergine mb-4">
          My Learning
        </div>
        <h1 className="font-display text-4xl text-ink leading-tight mb-2">
          My Courses
        </h1>
        {user && (
          <p className="text-slate2 text-base mb-10">
            Signed in as <span className="text-ink font-medium">{user.email}</span>
          </p>
        )}

        {enrolled === null && (
          <div className="animate-pulse h-24 rounded-2xl bg-ink/5" />
        )}

        {enrolled === true && (
          <div className="rounded-2xl border border-ink/10 bg-cream/50 p-6 flex flex-col sm:flex-row sm:items-center gap-5">
            <div className="flex-shrink-0 w-12 h-12 rounded-xl bg-brand-deep/10 flex items-center justify-center">
              <FaBookOpen className="text-brand-deep text-xl" />
            </div>
            <div className="flex-1">
              <p className="text-[11px] tracking-[0.18em] uppercase text-brand-aubergine mb-1">
                Generative AI for Parents
              </p>
              <p className="text-slate2 text-sm leading-relaxed">
                You're enrolled. Pick up where you left off.
              </p>
            </div>
            <Link
              to={resumeHref}
              className="inline-flex items-center gap-2 bg-ink text-bone text-sm font-medium px-4 py-2.5 rounded-full hover:bg-brand-deep transition-colors whitespace-nowrap flex-shrink-0"
            >
              Continue course <FaArrowRight className="text-[9px]" />
            </Link>
          </div>
        )}

        {enrolled === false && (
          <div className="py-6 text-slate2 text-sm">
            You haven't enrolled in any courses yet.{' '}
            <Link to="/courses/ai-for-parents" className="text-ink underline underline-offset-2 hover:text-brand-deep">
              Browse our courses
            </Link>{' '}
            to get started.
          </div>
        )}

        <SignOutPanel signOut={signOut} />
      </div>
    </div>
  );
}
